import { CountryCode } from "@hermes/locale";
import {
    SkuApplied,
    VtoCameraDimension,
    isSku,
} from "@hermes/states/vto-beauty";

import {
    CSS_DESKTOP_CAMERA_MAX_HEIGHT,
    CSS_MOBILE_CONFIGURATOR_HEIGHT,
    CSS_MOBILE_LAYOUT_WIDTH,
    IMAGE_RATIO_169,
    IMAGE_RATIO_34,
    MODULE_CAMERA_MAX_HEIGHT,
    MODULE_CAMERA_MAX_WIDTH,
    MODULE_CAMERA_MIN_HEIGHT,
    MODULE_CAMERA_MIN_WIDTH,
    SCENE7_DEFAULT_RESOLUTION,
    SCENE7_RESOLUTION_RANGES,
} from "../constants/vto-beauty-modal.constants";

/**
 * Get image ratio
 *
 * @param innerWidth window width
 * @returns image ratio
 */
export const getScene7ImageRatio = (
    innerWidth: number,
    innerHeight: number,
): string => {
    if (innerWidth < innerHeight) {
        return IMAGE_RATIO_34;
    }
    return IMAGE_RATIO_169;
};

/**
 * Get optimized resolution
 *
 * Ex : getScene7Resolution(1000) > 1080, getScene7Resolution(1081) > 1440
 *
 * @param innerWidth window width
 * @returns Range resolution
 */
export const getScene7Resolution = (innerWidth: number): number => {
    let previousResolution = SCENE7_DEFAULT_RESOLUTION;
    for (const range of SCENE7_RESOLUTION_RANGES) {
        previousResolution = range;
        if (innerWidth <= range) {
            break;
        }
    }
    return previousResolution;
};

/**
 * Get approval text according country
 *
 * @param country country code
 * @param privacyLink window.innerWidth
 * @returns Approval text
 */
export const getApprovalText = (
    country: CountryCode,
    privacyLink: string,
): string => {
    const LEGAL_TEXT_ACCORDING_COUNTRY: Partial<Record<CountryCode, string>> = {
        us: $localize`:@@hermes.product-page.vto-approval-text_USA:${privacyLink}:INTERPOLATION:`,
        cn: $localize`:@@hermes.product-page.vto-approval-text_CHINA:${privacyLink}:INTERPOLATION:`,
    };

    return (
        LEGAL_TEXT_ACCORDING_COUNTRY[country] ??
        $localize`:@@hermes.product-page.vto-approval-text:Access
        to your camera is necessary to use the virtual try-on service. 
        The data collected during its use are not stored, and are 
        automatically deleted when you close the service. You can
        access our ${privacyLink}:INTERPOLATION:. By using the virtual
        try-on service, you agree to give access to your camera. Hermes
        does not guarantee that the preview obtained when using the
        virtual try-on service is identical to the render when using
        the product tested.`
    );
};

/**
 * Calculate camera dimensions
 *
 * @param innerWidth window.innerWidth
 * @param innerHeight window.innerHeight
 * @returns Camera dimensions
 */
export const calculateCameraDimensions = (
    innerWidth: number,
    innerHeight: number,
): VtoCameraDimension => {
    const cameraHeightSpace = innerHeight - CSS_MOBILE_CONFIGURATOR_HEIGHT;

    let cameraWidth = MODULE_CAMERA_MAX_WIDTH;
    let cameraHeight = CSS_DESKTOP_CAMERA_MAX_HEIGHT;

    // if mobile layout
    if (innerWidth < CSS_MOBILE_LAYOUT_WIDTH) {
        // if portrait format
        if (innerHeight > innerWidth) {
            cameraHeight =
                cameraHeightSpace > MODULE_CAMERA_MAX_HEIGHT
                    ? MODULE_CAMERA_MAX_HEIGHT
                    : cameraHeightSpace;
            if (innerWidth < MODULE_CAMERA_MIN_WIDTH) {
                cameraWidth = MODULE_CAMERA_MIN_WIDTH;
            } else if (innerWidth < MODULE_CAMERA_MAX_WIDTH) {
                cameraWidth = innerWidth;
            }
        }
        // for landscape or portrait format
        if (cameraHeightSpace < MODULE_CAMERA_MIN_HEIGHT) {
            cameraHeight = MODULE_CAMERA_MIN_HEIGHT;
        }
    }

    return {
        width: cameraWidth,
        height: cameraHeight,
    };
};

export const getSelectedSku = (skuApplied: SkuApplied): string[] =>
    Object.values(skuApplied).filter((value) => isSku(value));
